'use client';

import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SearchContext } from '@/types/database';

interface SearchEmptyStatesProps {
  searchContext: SearchContext;
}

/**
 * Renders edge case empty states for different search intents.
 * Each state is designed as a brand-positive moment, not an error.
 */
export function SearchEmptyStates({ searchContext }: SearchEmptyStatesProps) {
  const router = useRouter();

  const handleBrowseAll = () => {
    router.push('/properties');
  };

  const handleSuggestedSearch = (query: string) => {
    const params = new URLSearchParams();
    params.set('q', query);
    router.push(`/properties?${params.toString()}`);
  };

  // No matches at all
  if (
    searchContext.intent === 'property_search' &&
    searchContext.resultCount === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h3 className="text-2xl font-serif text-primary mb-2">
          No perfect matches yet
        </h3>
        <p className="text-muted-foreground max-w-md">
          We couldn&apos;t find properties matching all your criteria. Try
          adjusting your requirements or explore our full collection.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={handleBrowseAll}
            className="rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
          >
            Browse All Properties
          </Button>
        </div>
        {/* Suggested broader searches */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 self-center">
            Try:
          </span>
          {['Apartments in Marina', 'Villas under 10M', 'New Launches'].map(
            (suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSuggestedSearch(suggestion)}
                className={cn(
                  'border border-primary/20 rounded-xl px-3 py-1.5',
                  'text-[11px] tracking-wider text-muted-foreground',
                  'hover:border-secondary hover:text-secondary',
                  'transition-all duration-200 cursor-pointer'
                )}
              >
                {suggestion}
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  // Outside our domain (rentals, commercial, office)
  if (searchContext.intent === 'unsupported') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h3 className="text-2xl font-serif text-primary mb-2">
          We specialize in premium residential sales
        </h3>
        <p className="text-muted-foreground max-w-md">
          Nova Realty&apos;s portfolio focuses on luxury residential properties
          for sale in Dubai&apos;s most prestigious communities.
        </p>
        <Button
          variant="outline"
          onClick={handleBrowseAll}
          className="mt-8 rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
        >
          Explore Our Collection
        </Button>
      </div>
    );
  }

  // Nonsensical or unrelated query
  if (searchContext.intent === 'cannot_parse') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h3 className="text-2xl font-serif text-primary mb-2">
          Let&apos;s find your ideal home
        </h3>
        <p className="text-muted-foreground max-w-md">
          Try describing what you&apos;re looking for — like a location,
          property type, or budget.
        </p>
        <div className="flex flex-wrap justify-center gap-2 mt-8">
          {[
            { label: 'Villas under 10M', query: 'Villas under 10 million' },
            {
              label: '3BR in Marina',
              query: '3 bedroom apartment in Dubai Marina',
            },
            { label: 'New Launches', query: 'New launch properties' },
          ].map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => handleSuggestedSearch(chip.query)}
              className={cn(
                'border border-primary/20 rounded-xl px-4 py-2',
                'text-xs tracking-wider text-muted-foreground',
                'hover:border-secondary hover:text-secondary',
                'transition-all duration-200 cursor-pointer'
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default fallback (shouldn't normally reach here)
  return null;
}
